import { useState, useEffect } from 'react'
import { mediaApi } from '../../api/media'
import type { StickerPack } from '../../api/media'

const RECENT_KEY = 'ggram-recent-stickers'
const MAX_RECENT = 16

interface RecentSticker { file_id: string; emoji: string }

function getRecent(): RecentSticker[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]') } catch { return [] }
}

function addRecent(item: RecentSticker) {
  const prev = getRecent().filter((s) => s.file_id !== item.file_id)
  localStorage.setItem(RECENT_KEY, JSON.stringify([item, ...prev].slice(0, MAX_RECENT)))
}

interface Props {
  onSelect: (fileId: string, emoji: string) => void
  onClose: () => void
  inline?: boolean
}

export function StickerPicker({ onSelect, onClose, inline }: Props) {
  const [packs, setPacks] = useState<StickerPack[]>([])
  const [activePack, setActivePack] = useState<number | 'recent'>('recent')
  const [loading, setLoading] = useState(true)
  const [recent, setRecent] = useState<RecentSticker[]>(getRecent)
  const [search, setSearch] = useState('')
  const [showBrowse, setShowBrowse] = useState(false)

  const reloadPacks = () => {
    mediaApi.getStickerPacks()
      .then((r) => {
        const res = r as { packs?: StickerPack[] }
        setPacks(res.packs ?? [])
        if (getRecent().length === 0) setActivePack(0)
      })
      .catch(() => {})
  }

  useEffect(() => {
    mediaApi.getStickerPacks()
      .then((r) => {
        const res = r as { packs?: StickerPack[] }
        setPacks(res.packs ?? [])
        if (getRecent().length === 0) setActivePack(0)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  if (showBrowse) {
    return <StickerPackBrowser onBack={() => { setShowBrowse(false); reloadPacks() }} inline={inline} />
  }

  const handleSelect = (fileId: string, emoji: string) => {
    addRecent({ file_id: fileId, emoji })
    setRecent(getRecent())
    onSelect(fileId, emoji)
    onClose()
  }

  const allItems = packs.flatMap((p) => p.items ?? [])
  const searchResults = search.trim()
    ? allItems.filter((item) => item.emoji?.toLowerCase().includes(search.toLowerCase()))
    : null

  const items = activePack === 'recent' ? [] : (packs[activePack as number]?.items ?? [])
  const displayItems = searchResults ?? (activePack === 'recent' ? recent : items)

  return (
    <div className={`w-72 bg-bg-primary border border-divider overflow-hidden z-30
      ${inline ? 'rounded-b-2xl border-t-0' : 'absolute bottom-full right-0 mb-2 rounded-2xl shadow-xl'}`}>
      {/* Search */}
      <div className="px-2 pt-2">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск стикеров…"
          className="w-full bg-bg-input text-txt-primary text-sm rounded-xl px-3 py-1.5 outline-none focus:ring-1 focus:ring-accent placeholder-txt-meta"
        />
      </div>
      {/* Pack tabs */}
      {!search && (
      <div className="flex gap-1 p-2 border-b border-divider overflow-x-auto">
        {/* Recent tab */}
        {recent.length > 0 && (
          <button
            onClick={() => setActivePack('recent')}
            className={`shrink-0 w-8 h-8 rounded-lg text-base ${activePack === 'recent' ? 'bg-accent/20 ring-1 ring-accent' : 'hover:bg-bg-hover'}`}
            title="Недавно использованные"
          >
            🕐
          </button>
        )}
        {packs.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setActivePack(i)}
            className={`shrink-0 w-8 h-8 rounded-lg text-xs ${activePack === i ? 'bg-accent/20 ring-1 ring-accent' : 'hover:bg-bg-hover'}`}
          >
            {p.thumbnail
              ? <img src={mediaApi.getURL(p.thumbnail)} className="w-full h-full object-cover rounded-lg" alt={p.name} />
              : '🎭'}
          </button>
        ))}
        {/* Browse packs */}
        <button
          onClick={() => setShowBrowse(true)}
          className="shrink-0 w-8 h-8 rounded-lg text-base hover:bg-bg-hover ml-auto"
          title="Обзор стикер-паков"
        >
          ➕
        </button>
      </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <span className="text-txt-meta text-sm">Loading stickers...</span>
        </div>
      ) : displayItems.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-32 gap-2">
          <span className="text-txt-meta text-sm">
            {search ? 'No stickers found' : activePack === 'recent' ? 'No recently used stickers' : 'No sticker packs installed'}
          </span>
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-1 p-2 max-h-48 overflow-y-auto">
          {displayItems.map((item) => {
            const fileId = item.file_id
            const emoji = item.emoji
            return (
              <button
                key={fileId}
                onClick={() => handleSelect(fileId, emoji)}
                className="aspect-square rounded-xl hover:bg-bg-hover transition-colors flex items-center justify-center p-1"
              >
                <img
                  src={mediaApi.getURL(fileId)}
                  alt={emoji}
                  className="w-full h-full object-contain"
                  loading="lazy"
                />
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

function StickerPackBrowser({ onBack, inline }: { onBack: () => void; inline?: boolean }) {
  const [packs, setPacks] = useState<StickerPack[]>([])
  const [installed, setInstalled] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [toggling, setToggling] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([mediaApi.getPublicStickerPacks(), mediaApi.getStickerPacks()])
      .then(([pub, mine]) => {
        const res = pub as { packs?: StickerPack[] }
        const myRes = mine as { packs?: StickerPack[] }
        setPacks(res.packs ?? [])
        setInstalled(new Set((myRes.packs ?? []).map((p) => p.id)))
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  const toggleInstall = async (packId: string) => {
    setToggling(packId)
    try {
      if (installed.has(packId)) {
        await mediaApi.uninstallStickerPack(packId)
        setInstalled((prev) => { const s = new Set(prev); s.delete(packId); return s })
      } else {
        await mediaApi.installStickerPack(packId)
        setInstalled((prev) => new Set([...prev, packId]))
      }
    } catch { /* ignore */ } finally {
      setToggling(null)
    }
  }

  return (
    <div className={`w-72 bg-bg-primary border border-divider overflow-hidden z-30
      ${inline ? 'rounded-b-2xl border-t-0' : 'absolute bottom-full right-0 mb-2 rounded-2xl shadow-xl'}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-divider">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary text-sm">←</button>
        <p className="text-txt-primary text-sm font-medium flex-1">Sticker Packs</p>
      </div>
      {loading ? (
        <div className="flex justify-center py-6">
          <div className="w-5 h-5 border-2 border-accent/30 border-t-accent rounded-full animate-spin" />
        </div>
      ) : packs.length === 0 ? (
        <p className="text-txt-meta text-sm text-center py-6">No packs available</p>
      ) : (
        <div className="max-h-56 overflow-y-auto divide-y divide-divider">
          {packs.map((pack) => {
            const isInstalled = installed.has(pack.id)
            return (
              <div key={pack.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="w-10 h-10 rounded-lg bg-bg-input overflow-hidden shrink-0 flex items-center justify-center">
                  {pack.thumbnail
                    ? <img src={mediaApi.getURL(pack.thumbnail)} className="w-full h-full object-cover" alt={pack.name} />
                    : <span className="text-xl">🎭</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-txt-primary text-sm font-medium truncate">{pack.name}</p>
                  <p className="text-txt-meta text-xs">{pack.items?.length ?? 0} stickers</p>
                </div>
                <button
                  onClick={() => toggleInstall(pack.id)}
                  disabled={toggling === pack.id}
                  className={`shrink-0 text-xs font-medium px-3 py-1.5 rounded-lg transition-colors disabled:opacity-40
                    ${isInstalled ? 'bg-txt-meta/10 text-txt-secondary hover:bg-red-400/10 hover:text-red-400' : 'bg-accent text-white hover:bg-blue-500'}`}
                >
                  {toggling === pack.id ? '…' : isInstalled ? 'Remove' : 'Add'}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
